import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useBaseStandardUi } from '../../lib/hooks/useBaseStandardUi';
import { useUserStore } from '../../lib/userStore';
import { useLeagueStore } from '../../lib/leagueStore';
import { supabase } from '../../lib/supabase';
import { hasAcceptedBaseLeagueRules } from '../../lib/baseLeagueRulesAcceptance';
import { BaseLeagueRulesOnboarding } from './BaseLeagueRulesOnboarding';

const RULES_PATH = '/league-rules-onboarding';
// Ces pages doivent rester accessibles, notamment pour quitter/changer de ligue.
const OUTSIDE_LEAGUE = new Set([
  '/auth', '/auth/bpm', '/auth/callback', '/dashboard', '/select-league',
  '/create-league', '/join-league', '/my-profile', '/update-username',
  '/league-payment-success', '/paiement',
]);

type Props = { children: ReactNode };
type League = { id: number; name: string };
type Status = 'loading' | 'error' | 'building' | 'complete';

/** Ne monte aucune lecture ni aucun contrôle supplémentaire dans les partenaires. */
export function BaseStandardBuildGuard({ children }: Props) {
  // Le type réel est lu dans le contrôle interne. Ici, true vérifie seulement
  // le périmètre (embed, tenant, session et ligues partenaires), pas le flag global Arsenal.
  const isBaseSite = useBaseStandardUi(true);
  const username = useUserStore(s => s.username);
  const userId = useUserStore(s => s.user_id);
  const league = useLeagueStore(s => s.selectedLeague);
  const { pathname } = useLocation();

  if (!isBaseSite || !username || !userId || !league || OUTSIDE_LEAGUE.has(pathname)
    || pathname.startsWith('/league-payment/') || pathname.startsWith('/admin/')) {
    return <>{children}</>;
  }

  // Nouvelle identité = nouvel état dès le premier rendu, avant les effets réseau.
  return <BaseBuildFlow key={`${userId}:${league.id}`} userId={userId} league={league}>{children}</BaseBuildFlow>;
}

function BaseBuildFlow({ userId, league, children }: Props & { userId: number; league: League }) {
  const { pathname } = useLocation();
  const [status, setStatus] = useState<Status>('loading');
  const [attempt, setAttempt] = useState(0);
  const [accepted, setAccepted] = useState(() => hasAcceptedBaseLeagueRules(userId, league.id));

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    setStatus('loading');

    async function loadBuildStatus() {
      try {
        const { data: mode, error: modeError } = await supabase.from('leagues')
          .select('league_type').eq('league_id', league.id).abortSignal(controller.signal).single();
        if (modeError || !mode) throw modeError ?? new Error('Ligue absente');
        if (mode.league_type !== 'standard_flexible') {
          if (!cancelled) setStatus('complete');
          return;
        }

        const { data: membership, error: membershipError } = await supabase.from('leagues_users')
          .select('initial_build_complete').eq('league_id', league.id).eq('user_id', userId)
          .abortSignal(controller.signal).single();
        if (membershipError || !membership) throw membershipError ?? new Error('Adhésion absente');
        if (!cancelled) setStatus(membership.initial_build_complete === true ? 'complete' : 'building');
      } catch {
        if (!cancelled) setStatus('error');
      } finally {
        window.clearTimeout(timeout);
      }
    }

    void loadBuildStatus();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [league.id, userId, attempt]);

  useEffect(() => {
    // BuildMyTeam émet déjà cet événement après la réussite de la mutation.
    // Bloquer le rendu pendant la relecture : le hook partagé peut encore indiquer
    // une construction. Revalider le couple courant évite qu'un événement tardif
    // de la ligue précédente ne libère par erreur la nouvelle ligue.
    const onComplete = () => {
      if (pathname === '/build-my-team' && status === 'building') {
        setStatus('loading');
        setAttempt(a => a + 1);
      }
    };
    window.addEventListener('dynastypool:buildComplete', onComplete);
    return () => window.removeEventListener('dynastypool:buildComplete', onComplete);
  }, [pathname, status]);

  if (status === 'loading' || status === 'error') {
    return (
      <div className="card shadow-lg border-0"><div className="card-body p-4 text-center">
        {status === 'loading' ? (
          <div role="status"><span className="spinner-border text-primary" aria-hidden="true" /><p>Vérification de votre équipe…</p></div>
        ) : (
          <div className="alert alert-danger" role="alert">
            <p>Impossible de vérifier le statut de votre équipe.</p>
            <button type="button" className="btn btn-outline-danger" onClick={() => setAttempt(a => a + 1)}>Réessayer</button>
          </div>
        )}
        <Link to="/select-league" className="btn btn-secondary mt-3">Retour aux ligues</Link>
      </div></div>
    );
  }

  if (status === 'complete') {
    // Évite aussi l'ancien habillage partenaire si cette URL est ouverte hors embed.
    return pathname === RULES_PATH ? <Navigate to="/my-office" replace /> : <>{children}</>;
  }
  if (!accepted) {
    return pathname === RULES_PATH
      ? <BaseLeagueRulesOnboarding userId={userId} league={league} onAccepted={() => setAccepted(true)} />
      : <Navigate to={RULES_PATH} replace />;
  }
  if (pathname !== '/build-my-team' && !pathname.startsWith('/players/')) {
    return <Navigate to="/build-my-team" replace />;
  }
  return <>{children}</>;
}
